<template>
    <div class="vx-flex vx-flex-col vx-gap-4 vx-p-6 vx-bg-slate-100" @click="markAsRead">
        <vx-alert
            v-if="!FEATURES.simple_message_hub && agentName && activeThread?.status === 'open'"
            :closeable="false"
            shadow
            class="vx-flex vx-items-center vx-p-4 vx-min-h-16"
        >
            <template v-slot:icon>
                <font-awesome-icon :icon="faPerson"></font-awesome-icon>
            </template>
            Assigned to
            <strong>
                <template v-if="fetchingAgents">
                    <vx-icon-loading v-if="fetchingAgents" spin></vx-icon-loading>
                </template>
                <span v-else>{{ agentName }}</span>
            </strong>
        </vx-alert>
        <conversation-input
            v-if="FEATURES.simple_message_hub || activeThread?.status !== 'closed'"
        ></conversation-input>
        <div
            v-if="!FEATURES.simple_message_hub"
            class="vx-flex vx-items-center vx-justify-between vx-gap-2"
            data-test="action-buttons"
        >
            <template v-if="activeThread?.status === 'open'">
                <vx-button
                    color="muted"
                    size="lg"
                    block
                    :disabled="actionInProgress || isMessagingDisabled"
                    :loading="changingThreadState"
                    @click="showCloseWithSnippetsModal = true"
                >
                    Close w/ Snippet <font-awesome-icon :icon="faMinusCircle"></font-awesome-icon>
                </vx-button>
                <vx-button
                    color="muted"
                    size="lg"
                    block
                    @click="changeThreadState('closed')"
                    :disabled="actionInProgress"
                    :loading="changingThreadState"
                >
                    Close <font-awesome-icon :icon="faBan"></font-awesome-icon>
                </vx-button>
                <message-builder-snippets
                    v-model:visible="showCloseWithSnippetsModal"
                    @selected="closeWithSnippet($event)"
                ></message-builder-snippets>
            </template>
            <vx-button
                v-if="activeThread?.status === 'closed'"
                color="muted"
                size="lg"
                block
                @click="changeThreadState('open')"
                :disabled="actionInProgress"
                :loading="changingThreadState"
            >
                Re-open <font-awesome-icon :icon="faFolderOpen"></font-awesome-icon>
            </vx-button>
        </div>
    </div>
</template>
<script setup>
import { faBan, faFolderOpen, faMinusCircle, faPerson } from '@fortawesome/pro-solid-svg-icons';
import ConversationInput from './ConversationInput.vue';
import MessageBuilderSnippets from '~/components/general/message-builder/modals/MessageBuilderSnippets.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxIconLoading, VxButton, VxSwal } from '@voxie/frontend-components';
import { inject } from 'vue';
import { useConversation } from '~/composables/useConversation';
import { catchRequestError } from '~/utils/request';
import { ref } from 'vue';
import { actionsClient } from '~/services';

const FEATURES = inject('FEATURES');

const {
    activeThread,
    fetchingAgents,
    changingThreadState,
    actionInProgress,
    agentName,
    markAsRead,
    claimThread,
    changeActiveThreadState,
    isMessagingDisabled,
    message,
    deliverMessage,
} = useConversation();

const changeThreadState = async (state, throwError) => {
    try {
        if (state === 'open') {
            await changeActiveThreadState(state);
            await claimThread();
            await markAsRead();
        } else {
            await claimThread();
            await markAsRead();
            await changeActiveThreadState(state);
        }
    } catch (e) {
        if (throwError) {
            throw e;
        }

        VxSwal.fire({
            title: 'Whoops!',
            text: catchRequestError(e),
            icon: 'error',
        });
    }
};

const showCloseWithSnippetsModal = ref(false);
const closeWithSnippet = async ({ body, media_url, media_content_type }) => {
    message.body = body;
    message.media_url = media_url;
    message.media_content_type = media_content_type;
    message.selectedSnippetBody = body;

    try {
        if (isMessagingDisabled.value) {
            return;
        }

        await deliverMessage(true);
        await changeThreadState('closed', true);
        actionsClient.send('snippet_used');
    } catch (e) {
        VxSwal.fire({
            title: 'Whoops!',
            text: catchRequestError(e),
            icon: 'error',
        });
    }
};
</script>
